import * as React from "react"

import {
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnOrderState,
  type ColumnSizingState,
  type ExpandedState,
  type OnChangeFn,
  type SortingState,
  type ColumnDef as TanstackColumnDef,
  type VisibilityState,
} from "@tanstack/react-table"

import {
  DEFAULT_COLUMN_WIDTH,
  expandedIdsToRecord,
  reconcileColumnOrder,
  recordToExpandedIds,
  renderFallback,
  renderId,
} from "./TableInternals"
import type { ColumnDef, TableProps } from "./types"

type UseTableControllerParams<T> = Pick<
  TableProps<T>,
  | "columnOrder"
  | "columns"
  | "columnVisibility"
  | "data"
  | "defaultExpanded"
  | "defaultSort"
  | "expandedIds"
  | "getRowId"
  | "getSubRows"
  | "onColumnOrderChange"
  | "onColumnVisibilityChange"
  | "onExpandedChange"
  | "onSortChange"
>

export function useTableController<T>({
  columnOrder,
  columns,
  columnVisibility,
  data,
  defaultExpanded,
  defaultSort,
  expandedIds,
  getRowId,
  getSubRows,
  onColumnOrderChange,
  onColumnVisibilityChange,
  onExpandedChange,
  onSortChange,
}: UseTableControllerParams<T>) {
  // Lookup of the source column def by id. The presentational facts
  // (variant / align / minWidth) live here, not on the TanStack column.
  const columnsById = React.useMemo(() => {
    const map = new Map<string, ColumnDef<T>>()
    for (const col of columns) {
      map.set(col.id, col)
    }
    return map
  }, [columns])

  // Default display order pushes `id` columns to the far right,
  // preserving the relative order of everything else. Consumers can declare
  // `id` anywhere and override the whole order via `columnOrder`.
  const defaultColumnOrder = React.useMemo<ColumnOrderState>(
    () => [
      ...columns.filter((col) => col.variant !== "id").map((col) => col.id),
      ...columns.filter((col) => col.variant === "id").map((col) => col.id),
    ],
    [columns],
  )

  const tanstackColumns = React.useMemo<TanstackColumnDef<T>[]>(
    () =>
      columns.map((col) => {
        const sortable = Boolean(col.sortable)
        // TanStack gates `getCanSort()` / `getToggleSortingHandler()` on the
        // column having an accessor — even in manual mode. A cell-only column
        // (no `accessorKey`) would therefore render an inert, unclickable
        // header despite `sortable: true`. Give sortable columns a fallback
        // accessor that reads the field named by the column id so the header
        // is genuinely sortable: in manual mode the value is unused (the
        // consumer sorts from the emitted descriptor); in client mode it sorts
        // by that field. A column may carry an `accessorKey` or an
        // `accessorFn`, never both.
        const accessor = col.accessorKey
          ? { accessorKey: col.accessorKey }
          : sortable
            ? {
                accessorFn: (row: T) =>
                  (row as Record<string, unknown>)[col.id],
              }
            : {}
        return {
          id: col.id,
          ...accessor,
          header:
            typeof col.header === "function" ? col.header : () => col.header,
          cell: col.cell
            ? (ctx) => col.cell?.(ctx.row.original)
            : col.variant === "id"
              ? (ctx) => renderId(ctx.getValue())
              : (ctx) => renderFallback(ctx.getValue()),
          size: col.width ?? DEFAULT_COLUMN_WIDTH,
          minSize: col.minWidth,
          enableSorting: sortable,
          enableHiding: col.enableHiding ?? true,
          enableResizing: col.enableResizing ?? true,
          enableReordering: col.enableReordering ?? true,
        }
      }),
    [columns],
  )

  // Single-column sorting. Omitting `onSortChange` sorts client-side; providing
  // it switches to manual mode — TanStack stops sorting and we emit the active
  // descriptor (or null when cleared) for the consumer to fetch server-side.
  // Initial sort: `defaultSort` if given, else ascending by the first sortable
  // column (none, if no column is sortable).
  const [sorting, setSorting] = React.useState<SortingState>(() => {
    if (defaultSort) {
      return [
        { id: defaultSort.columnId, desc: defaultSort.direction === "desc" },
      ]
    }
    const firstSortableId = defaultColumnOrder.find(
      (id) => columnsById.get(id)?.sortable,
    )
    return firstSortableId ? [{ id: firstSortableId, desc: false }] : []
  })

  // `defaultSort` seeds the initial sort, but a consumer in manual mode
  // (`onSortChange`) can also change it externally — e.g. applying a saved view
  // that persists a different sort column. Keep the header indicator in sync by
  // resetting `sorting` whenever `defaultSort` changes to a new descriptor
  // (React's "reset state when a prop changes" pattern — no effect, no flash).
  // A consumer with a stable `defaultSort` never triggers this, so header-click
  // and client-side sorting are unaffected.
  const defaultSortKey = defaultSort
    ? `${defaultSort.columnId}:${defaultSort.direction}`
    : ""
  const [prevDefaultSortKey, setPrevDefaultSortKey] =
    React.useState(defaultSortKey)
  if (defaultSort && defaultSortKey !== prevDefaultSortKey) {
    setPrevDefaultSortKey(defaultSortKey)
    setSorting([
      { id: defaultSort.columnId, desc: defaultSort.direction === "desc" },
    ])
  }

  const handleSortingChange = React.useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      setSorting((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater
        onSortChange?.(
          next.length > 0
            ? { columnId: next[0].id, direction: next[0].desc ? "desc" : "asc" }
            : null,
        )
        return next
      })
    },
    [onSortChange],
  )

  // Column visibility / order are controlled when the matching prop is given,
  // otherwise internal (order seeded with the id-far-right default). Changes are
  // always emitted so a parent (e.g. TableColumnToggle's reorder list) can lift
  // the state up. Column sizing (resize) is always internal.
  const [internalVisibility, setInternalVisibility] =
    React.useState<VisibilityState>({})
  const visibility = columnVisibility ?? internalVisibility
  const handleVisibilityChange = React.useCallback<OnChangeFn<VisibilityState>>(
    (updater) => {
      const next = typeof updater === "function" ? updater(visibility) : updater
      // Only own the state when uncontrolled — writing internal state in
      // controlled mode is a redundant render and leaks the last controlled
      // value if the consumer later drops the prop.
      if (!columnVisibility) {
        setInternalVisibility(next)
      }
      onColumnVisibilityChange?.(next)
    },
    [visibility, columnVisibility, onColumnVisibilityChange],
  )

  const [internalOrder, setInternalOrder] =
    React.useState<ColumnOrderState>(defaultColumnOrder)
  // Reconcile the stored (uncontrolled) order against the current columns so a
  // dynamic `columns` change stays in sync: keep stored positions for columns
  // that still exist, drop ids that no longer do, and splice newly-added ids in
  // at their default index, which keeps `id` columns far right by default.
  const order = React.useMemo(
    () =>
      columnOrder ?? reconcileColumnOrder(internalOrder, defaultColumnOrder),
    [columnOrder, internalOrder, defaultColumnOrder],
  )
  const handleOrderChange = React.useCallback<OnChangeFn<ColumnOrderState>>(
    (updater) => {
      const next = typeof updater === "function" ? updater(order) : updater
      if (!columnOrder) {
        setInternalOrder(next)
      }
      onColumnOrderChange?.(next)
    },
    [order, columnOrder, onColumnOrderChange],
  )

  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>({})

  // Nesting expansion. `getSubRows` opts a column tree in; the expanded model
  // flattens visible descendants into the row model, so they virtualize like
  // any other row. Controlled when `expandedIds` is given, else internal
  // (seeded from `defaultExpanded`). Expressed to consumers as an array of
  // expanded parent row ids (mirrors `columnOrder`); converted to TanStack's
  // ExpandedState record here. Changes are always emitted so a parent can drive
  // Expand/Collapse-All or persist the open sections.
  const [internalExpanded, setInternalExpanded] = React.useState<
    Record<string, boolean>
  >(() => expandedIdsToRecord(defaultExpanded))
  const expandedRecord = expandedIds
    ? expandedIdsToRecord(expandedIds)
    : internalExpanded
  const handleExpandedChange = React.useCallback<OnChangeFn<ExpandedState>>(
    (updater) => {
      const next =
        typeof updater === "function" ? updater(expandedRecord) : updater
      // We never expose a toggle-all control, so per-row toggles keep the state
      // a record; guard the `true` (all-expanded) case defensively as a no-op.
      const nextRecord = next === true ? expandedRecord : next
      if (!expandedIds) {
        setInternalExpanded(nextRecord)
      }
      onExpandedChange?.(recordToExpandedIds(nextRecord))
    },
    [expandedRecord, expandedIds, onExpandedChange],
  )
  const nestable = Boolean(getSubRows)

  const table = useReactTable({
    data,
    columns: tanstackColumns,
    getRowId: (row) => getRowId(row),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getSubRows,
    getExpandedRowModel: getExpandedRowModel(),
    state: {
      sorting,
      columnVisibility: visibility,
      columnOrder: order,
      columnSizing,
      expanded: expandedRecord,
    },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: handleVisibilityChange,
    onColumnOrderChange: handleOrderChange,
    onColumnSizingChange: setColumnSizing,
    onExpandedChange: handleExpandedChange,
    manualSorting: Boolean(onSortChange),
    // In manual mode the data is always in *some* server order, so a third
    // header click clearing the sort (TanStack's default) would desync: the
    // consumer can't act on a null descriptor, and the Table can't recover its
    // own indicator from an unchanged `defaultSort` key. Disable removal so
    // manual sorting is a clean asc↔desc toggle; client-side keeps its
    // asc→desc→unsorted cycle.
    enableSortingRemoval: !onSortChange,
    enableMultiSort: false,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
  })

  return { columnsById, nestable, table }
}
